import { Link, usePage } from "@inertiajs/react";
import { Home, Users } from "lucide-react";
import * as React from "react";
import { AccountSwitcher } from "@/bizminer/layouts/app/sidebar/account-switcher.tsx";
import { NavAccount } from "@/bizminer/layouts/app/sidebar/nav-account.tsx";
import { getIcon } from "@/lib/icons";
import { cn } from "@/lib/utils.ts";
import {
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
} from "@/components/ui/sidebar.tsx";
import {
  bizminer_app_account_root_path,
  bizminer_app_pillar_insight_functional_insights_path,
  bizminer_app_pillar_insight_improvement_levers_path,
  bizminer_app_pillar_insight_path,
  bizminer_app_pillar_insight_swot_path,
  scoring_bizminer_app_pillar_insight_path,
} from "@/routes";
import type { BizminerInertiaSharedProps } from "@/types/inertia.ts";

interface AccountNavigationProps {
  isAccountPage: boolean;
}

export function AccountNavigation({ isAccountPage }: AccountNavigationProps) {
  const {
    props: { sidebar, pillar_insight },
    component,
  } = usePage<BizminerInertiaSharedProps & { pillar_insight?: { id: number } }>();
  const currentPillarId = pillar_insight?.id;

  const isHomeActive = component === "Bizminer/App/Accounts/Show";

  const pillarInsights = React.useMemo(() => {
    if (!sidebar.unlocked_pillars) return [];

    return sidebar.unlocked_pillars.map((pillar) => {
      const IconComponent = getIcon(pillar.icon) ?? Users;
      const isCurrentPillar = currentPillarId === pillar.id;
      const isShowPage = component === "Bizminer/App/PillarInsights/Show";
      const isSwotPage = component === "Bizminer/App/PillarInsights/Swot/Show";
      const isScoringPage = component === "Bizminer/App/PillarInsights/Scoring";
      const isImprovementLeversPage =
        component === "Bizminer/App/PillarInsights/ImprovementLevers/Index" ||
        component ===
          "Bizminer/App/PillarInsights/ImprovementLevers/DeepDive" ||
        component ===
          "Bizminer/App/PillarInsights/ImprovementLevers/Overview" ||
        component === "Bizminer/App/PillarInsights/ImprovementLevers/Actions";
      const isFunctionalInsightsPage =
        component ===
          "Bizminer/App/PillarInsights/FunctionalInsights/Overview" ||
        component ===
          "Bizminer/App/PillarInsights/FunctionalInsights/KpiDrivers" ||
        component ===
          "Bizminer/App/PillarInsights/FunctionalInsights/DeepDive" ||
        component === "Bizminer/App/PillarInsights/FunctionalInsights/Index";

      return {
        title: pillar.title,
        icon: IconComponent,
        url: bizminer_app_pillar_insight_path(pillar.id),
        isActive: isCurrentPillar,
        items: [
          {
            title: "Summary",
            url: bizminer_app_pillar_insight_path(pillar.id),
            isActive: isShowPage && isCurrentPillar,
          },
          {
            title: "Scoring & Assessment",
            url: scoring_bizminer_app_pillar_insight_path(pillar.id),
            isActive: isScoringPage && isCurrentPillar,
          },
          {
            title: "Functional Insights",
            url: bizminer_app_pillar_insight_functional_insights_path(
              pillar.id,
            ),
            isActive: isFunctionalInsightsPage && isCurrentPillar,
          },
          {
            title: "Competitive Analysis",
            url: bizminer_app_pillar_insight_swot_path(pillar.id),
            isActive: isSwotPage && isCurrentPillar,
          },
          {
            title: "Improvement Levers",
            url: bizminer_app_pillar_insight_improvement_levers_path(pillar.id),
            isActive: isImprovementLeversPage && isCurrentPillar,
          },
        ],
      };
    });
  }, [component, currentPillarId, sidebar.unlocked_pillars]);

  return (
    <div
      className={cn(
        "transition-opacity duration-200",
        isAccountPage ? "opacity-100" : "opacity-60",
      )}
    >
      <SidebarGroup>
        <SidebarGroupLabel>Current Account</SidebarGroupLabel>
        <SidebarGroupContent>
          <AccountSwitcher
            accounts={sidebar.accounts!}
            currentAccount={sidebar.current_account!}
          />
        </SidebarGroupContent>
      </SidebarGroup>

      <SidebarGroup>
        <SidebarGroupContent>
          <SidebarMenu>
            <SidebarMenuItem>
              <SidebarMenuButton asChild isActive={isHomeActive}>
                <Link href={bizminer_app_account_root_path()}>
                  <Home />
                  <span>Account Overview</span>
                </Link>
              </SidebarMenuButton>
            </SidebarMenuItem>
          </SidebarMenu>
        </SidebarGroupContent>
      </SidebarGroup>

      {isAccountPage && <NavAccount items={pillarInsights} />}
    </div>
  );
}
